///
/// EarthBrowser
///
/// Created by Matt Giger on 12/2/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type AnyGestureRecognizer, DiscreteGestureRecognizer, GestureState, type TouchPoint } from "./GestureRecognizer"

/**
 * Entry in the arena tracking a gesture recognizer and its state
 */
interface ArenaEntry {
	recognizer: AnyGestureRecognizer
	// Whether this recognizer has "won" the arena for its gesture type
	isActive: boolean
	// Touch IDs this recognizer is tracking
	touchIds: Set<number>
	// Cached closest hit distance for this recognizer's owner (lower = closer = higher priority)
	closestHitDistance: number
}

/**
 * Priority a recognizer competes with once its owner has been hit: the base
 * priority plus a boost for proximity, so closer objects win over farther
 * ones unless the base priorities differ widely.
 *
 * distanceBoost = 500 / (1 + hitDistance * 0.0001): ~500 for very close
 * hits, ~250 at 10,000 units. At distances around 7000-8000 a difference
 * of ~650 units is ~10-11 points, enough to overcome a 10-point base gap.
 * Shared by the arena (touch gestures) and the hover routing in
 * GestureSystem so hover and tap agree on which object the pointer is over
 */
export function effectiveGesturePriority(basePriority: number, hitDistance: number): number {
	if (hitDistance === Infinity) {
		return basePriority
	}
	return basePriority + 500 / (1 + hitDistance * 0.0001)
}

/**
 * GestureArena - Manages conflict resolution between competing gesture recognizers
 *
 * This is inspired by Flutter's gesture arena pattern combined with Apple's
 * simultaneous recognition concept. The key innovations:
 *
 * 1. **Touch-level tracking**: Tracks which touches each recognizer is interested in
 * 2. **Simultaneous recognition**: Allows compatible gestures to work together
 * 3. **Priority-based resolution**: When gestures conflict, uses priority to decide
 * 4. **Viewport-level gestures**: Supports gestures that operate regardless of
 *    which objects individual touches land on (solving the pinch-on-selected-object problem)
 *
 * Usage:
 * ```typescript
 * const arena = new GestureArena()
 *
 * // Viewport-level pinch (always handles zoom, regardless of touched objects)
 * const viewportPinch = new PinchGestureRecognizer()
 * viewportPinch.priority = 100 // High priority for viewport gestures
 * arena.addRecognizer(viewportPinch)
 *
 * // Object-level pan (for dragging individual objects)
 * const objectPan = new PanGestureRecognizer()
 * objectPan.priority = 50
 * arena.addRecognizer(objectPan)
 * ```
 */
export class GestureArena {
	private entries: ArenaEntry[] = []
	private activeTouches = new Map<number, TouchPoint>()

	constructor(recognizers: AnyGestureRecognizer[] = []) {
		this.addRecognizers(recognizers)
	}

	addRecognizers(recognizers: AnyGestureRecognizer[]): void {
		recognizers.forEach(r => {
			if(this.entries.some(e => e.recognizer === r)) {
				return
			}
			// Only reset recognizers in Ended or Cancelled state
			// Don't reset Failed or Possible state - they might be part of an ongoing gesture sequence
			// (e.g., multi-tap waiting for additional taps between touch sessions)
			if(r.state === GestureState.Ended || r.state === GestureState.Cancelled) {
				r.reset()
			}
			// Clear suppression from previous gesture sessions for discrete recognizers
			// This doesn't reset the recognizer's internal state (like tapCount for multi-tap)
			if (r instanceof DiscreteGestureRecognizer) {
				r.clearSuppression()
			}
			this.entries.push({
				recognizer: r,
				isActive: false,
				touchIds: new Set(),
				closestHitDistance: Infinity
			})
		})

		// Sort by effective priority (factors in hit distance)
		this.sortEntriesByEffectivePriority()
	}

	/**
	 * Calculate effective priority for an entry.
	 * Combines base priority with hit distance - closer objects get higher priority.
	 *
	 * Formula: effectivePriority = basePriority + distanceBoost
	 * where distanceBoost = 500 / (1 + hitDistance * 0.0001)
	 *
	 * This gives a boost of ~500 for very close hits, tapering off for distant hits.
	 * The large maximum boost ensures that distance differences can overcome
	 * significant base priority differences. For example, at distances around
	 * 7000-8000, a difference of ~650 units results in ~10-11 point boost difference,
	 * which can overcome a 10-point base priority gap.
	 */
	private getEffectivePriority(entry: ArenaEntry): number {
		return effectiveGesturePriority(entry.recognizer.priority, entry.closestHitDistance)
	}

	/**
	 * Sort entries by effective priority (highest first)
	 */
	private sortEntriesByEffectivePriority(): void {
		this.entries.sort((a, b) => this.getEffectivePriority(b) - this.getEffectivePriority(a))
	}

	/**
	 * Get recognizers that are currently active (began or changed state)
	 */
	getActiveRecognizers(): AnyGestureRecognizer[] {
		return this.entries
			.filter(e => e.recognizer.isActive())
			.map(e => e.recognizer)
	}

	/**
	 * Process a touch start event
	 * Distributes to all interested recognizers, then resolves conflicts
	 */
	touchStart(touches: TouchPoint[]): void {
		// Register new touches
		touches.forEach(t => {
			this.activeTouches.set(t.id, t)
		})

		// Give all recognizers a chance to claim the touches
		for (const entry of this.entries) {
			if (!entry.recognizer.enabled) continue

			// Add new touches to this recognizer's tracked set
			touches.forEach(t => entry.touchIds.add(t.id))

			// Calculate closest hit distance for this recognizer's owner
			this.updateClosestHitDistance(entry, touches)

			// Send touch to recognizer
			entry.recognizer.handleTouchStart(touches)
		}

		// Re-sort after hit distances are calculated
		this.sortEntriesByEffectivePriority()

		// Clear stale isActive flags for recognizers that cancelled/failed during handleTouchStart
		for (const entry of this.entries) {
			if (entry.isActive && !entry.recognizer.isActive()) {
				entry.isActive = false
			}
		}

		this.resolveConflicts()
	}

	/**
	 * Update the closest hit distance for an entry based on touch gesture hits
	 */
	private updateClosestHitDistance(entry: ArenaEntry, touches: TouchPoint[]): void {
		const owner = entry.recognizer.owner
		if (!owner) return

		for (const touch of touches) {
			// Find the hit for this recognizer's owner
			const hit = touch.gestureHits.find(h => h.node === owner)
			if (hit && hit.distance < entry.closestHitDistance) {
				entry.closestHitDistance = hit.distance
			}
		}
	}

	/**
	 * Process a touch move event
	 */
	touchMove(touches: TouchPoint[]): void {
		// Update active touches
		touches.forEach(t => {
			const existing = this.activeTouches.get(t.id)
			if (existing) {
				existing.x = t.x
				existing.y = t.y
			}
		})

		// Send to all interested recognizers
		for (const entry of this.entries) {
			if (!entry.recognizer.enabled) continue

			// Only send if this recognizer has relevant touches
			const relevantTouches = touches.filter(t => entry.touchIds.has(t.id))
			if (relevantTouches.length > 0) {
				entry.recognizer.handleTouchMove(relevantTouches)
			}
		}

		this.resolveConflicts()
	}

	/**
	 * Process a touch end event
	 */
	touchEnd(touches: TouchPoint[]): void {
		// Track which touch IDs are ending (needed for suppression logic)
		const endingTouchIds = new Set(touches.map(t => t.id))

		// Send to all interested recognizers (but don't remove touches yet)
		for (const entry of this.entries) {
			if (!entry.recognizer.enabled) continue

			// Only send if this recognizer has relevant touches
			const relevantTouches = touches.filter(t => entry.touchIds.has(t.id))
			if (relevantTouches.length > 0) {
				entry.recognizer.handleTouchEnd(relevantTouches)
			}
		}

		// Resolve conflicts BEFORE removing touches (needed for suppress logic)
		this.resolveConflicts()

		// Now remove ended touches from tracking
		for (const entry of this.entries) {
			endingTouchIds.forEach(id => entry.touchIds.delete(id))
		}

		// Remove from active touches
		touches.forEach(t => this.activeTouches.delete(t.id))

		// Reset recognizers that are in terminal state and have no more touches
		for (const entry of this.entries) {
			if (entry.recognizer.isTerminal() && entry.touchIds.size === 0) {
				entry.recognizer.reset()
				entry.isActive = false
			}
		}
	}

	/**
	 * Process a touch cancel event
	 */
	touchCancel(): void {
		// Cancel all recognizers
		for (const entry of this.entries) {
			entry.recognizer.handleTouchCancel()
			entry.touchIds.clear()
			entry.isActive = false
		}

		this.activeTouches.clear()
	}

	/**
	 * Reset all recognizers and clear state
	 */
	reset(): void {
		for (const entry of this.entries) {
			entry.recognizer.reset()
			entry.touchIds.clear()
			entry.isActive = false
		}
		this.activeTouches.clear()
	}

	/**
	 * Resolve conflicts between competing recognizers
	 * This is called after each touch event to determine winners
	 *
	 * Key principle: Once a recognizer has "won" (isActive = true), it owns the
	 * gesture until it ends or is cancelled. It should not be cancelled by
	 * other recognizers trying to compete.
	 */
	private resolveConflicts(): void {
		// Get all recognizers in active gesture state (Began, Changed, or Ended for discrete gestures)
		const inGestureState = this.entries.filter(e => {
			const state = e.recognizer.state
			return state === GestureState.Began || state === GestureState.Changed || state === GestureState.Ended
		})

		if (inGestureState.length === 0) {
			return
		}

		// Separate into already-won vs newly-competing
		const alreadyActive = inGestureState.filter(e => e.isActive)
		const newlyCompeting = inGestureState.filter(e => !e.isActive)

		// Already-active recognizers keep their status - they've won and own the gesture
		// No need to do anything for them

		// If there are no newly competing recognizers, we're done
		if (newlyCompeting.length === 0) {
			return
		}

		// Check if new recognizers conflict with already-active ones
		for (const newEntry of newlyCompeting) {
			// Check if this new recognizer overlaps with any already-active one
			const conflictsWithActive = alreadyActive.some(active => {
				const hasOverlap = Array.from(newEntry.touchIds).some(id => active.touchIds.has(id))
				return hasOverlap && !active.recognizer.allowSimultaneous
			})

			if (conflictsWithActive) {
				// An already-active recognizer owns these touches - cancel the new one
				newEntry.recognizer.cancel()
				newEntry.isActive = false
			} else {
				// No conflict with active recognizers - this one can proceed
				newEntry.isActive = true
			}
		}

		// Now resolve conflicts among the newly-activated recognizers
		const justActivated = newlyCompeting.filter(e => e.isActive)
		if (justActivated.length <= 1) {
			if (justActivated.length === 1) {
				const winner = justActivated[0]
				winner.recognizer.didBecomeActive()

				// If winner is discrete, suppress other discrete gestures that share touches
				if (winner.recognizer instanceof DiscreteGestureRecognizer) {
					this.suppressCompetingDiscreteGestures(winner)
				}
			}
			return
		}

		// Group by touch overlap and resolve
		const groups = this.groupCompatibleRecognizers(justActivated)

		for (const group of groups) {
			if (group.length === 1) {
				// Single recognizer in group - it wins by default, notify it
				const winner = group[0]
				winner.recognizer.didBecomeActive()

				// If winner is a discrete gesture, suppress ALL other discrete gestures
				// that share touches (including deferred ones not yet in Ended state)
				if (winner.recognizer instanceof DiscreteGestureRecognizer) {
					this.suppressCompetingDiscreteGestures(winner)
				}
				continue
			}

			// Check if all in group allow simultaneous
			const allAllowSimultaneous = group.every(e => e.recognizer.allowSimultaneous)

			if (allAllowSimultaneous && this.checkDelegateAllowsSimultaneous(group)) {
				// All can be active simultaneously - notify each
				group.forEach(e => e.recognizer.didBecomeActive())
				continue
			}

			// Need to pick a winner among the new competitors
			const winner = this.pickWinner(group)

			// Notify winner
			winner.recognizer.didBecomeActive()

			// Cancel the losers in this group
			for (const entry of group) {
				if (entry !== winner) {
					entry.recognizer.cancel()
					entry.isActive = false
				}
			}

			// If winner is a discrete gesture, suppress ALL other discrete gestures
			// that share touches (including deferred ones not yet in Ended state)
			if (winner.recognizer instanceof DiscreteGestureRecognizer) {
				this.suppressCompetingDiscreteGestures(winner)
			}
		}
	}

	/**
	 * Suppress all discrete gestures that compete with the winner
	 * This prevents deferred recognitions from firing later
	 */
	private suppressCompetingDiscreteGestures(winner: ArenaEntry): void {
		for (const entry of this.entries) {
			if (entry === winner) continue
			if (!(entry.recognizer instanceof DiscreteGestureRecognizer)) continue

			// Check if this entry shares any touches with the winner
			const sharesTouches = Array.from(winner.touchIds).some(id => entry.touchIds.has(id))
			if (sharesTouches) {
				;(entry.recognizer as DiscreteGestureRecognizer).suppress()
			}
		}
	}

	/**
	 * Group recognizers by touch overlap
	 * Recognizers tracking different touch sets don't conflict
	 */
	private groupCompatibleRecognizers(entries: ArenaEntry[]): ArenaEntry[][] {
		const groups: ArenaEntry[][] = []
		const assigned = new Set<ArenaEntry>()

		for (const entry of entries) {
			if (assigned.has(entry)) continue

			const group = [entry]
			assigned.add(entry)

			// Find others that share touches with this entry
			for (const other of entries) {
				if (assigned.has(other)) continue

				// Check for touch overlap
				const hasOverlap = Array.from(entry.touchIds).some(id => other.touchIds.has(id))
				if (hasOverlap) {
					group.push(other)
					assigned.add(other)
				}
			}

			groups.push(group)
		}

		return groups
	}

	/**
	 * Check if delegate allows all recognizers in group to work simultaneously
	 */
	private checkDelegateAllowsSimultaneous(group: ArenaEntry[]): boolean {

		// Check all pairs
		// for (let i = 0; i < group.length; i++) {
		// 	for (let j = i + 1; j < group.length; j++) {
		// 		const result = this.delegate.shouldRecognizeSimultaneously(
		// 			group[i].recognizer,
		// 			group[j].recognizer
		// 		)
		// 		if (result === false) return false
		// 	}
		// }

		return true
	}

	/**
	 * Pick a winner from competing recognizers
	 */
	private pickWinner(entries: ArenaEntry[]): ArenaEntry {
		// Sort by effective priority and pick highest
		const sorted = [...entries].sort((a, b) =>
			this.getEffectivePriority(b) - this.getEffectivePriority(a)
		)

		// Debug logging
		// console.log(`[Arena] Winner: ${sorted[0].recognizer.constructor.name}`)

		return sorted[0]
	}
}
